import React from 'react';
import Drawer from '../../ui/Drawer';
import Badge from '../../ui/Badge';
import { useOfferNotifications } from '../hooks/useOfferNotifications';
import { relativeTime } from '../utils';
import {
  contingencySubject,
  getNotificationMeta,
  notificationContingencyId,
  notificationDestination,
  notificationText,
} from './notificationTypes';
import './NotificationsDrawer.css';

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Older'];

/** Wisconsin deadlines and notices are stated in Central time (copy §6.1). */
export const WI_TIME_ZONE = 'America/Chicago';

/**
 * "Sent Oct 12, 2026": a reminder's {when} is relative to when it was sent
 * (copy §6.1). The reminders are Wisconsin's, so the date is the Central-time
 * date, whatever the reader's own time zone.
 */
export function sentDateLabel(value, timeZone = WI_TIME_ZONE) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Sent ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone })}`;
}

function NotificationItem({ notif, onOpenHistory, onNavigate, onMarkRead, onClose }) {
  const meta = getNotificationMeta(notif.notification_type, notif);
  const canNavigate = !!(notificationDestination(notif) && onNavigate);
  const canOpenHistory = !!(notif.offer_id && onOpenHistory);
  const interactive = canNavigate || canOpenHistory;
  const sentLabel = meta.reminder ? sentDateLabel(notif.created_at || notif.sent_at) : null;
  const subject = contingencySubject(notif);
  const contingencyId = notificationContingencyId(notif);

  const handleClick = () => {
    if (!notif.is_read) onMarkRead(notif.id);
    if (canNavigate) {
      const { tab, params } = notificationDestination(notif);
      onNavigate(tab, params);
    } else if (canOpenHistory) {
      onOpenHistory({ offerId: notif.offer_id });
    }
    if (interactive && onClose) onClose();
  };

  const inner = (
    <>
      <span className="offers-notif-item__icon">
        <span aria-hidden="true">{meta.icon}</span>
        <span className="offers-sr-only">{meta.label}</span>
      </span>
      <span className="offers-notif-item__body">
        {subject && (
          <span
            className="offers-notif-item__subject"
            data-testid="notif-contingency-subject"
            data-contingency-id={contingencyId || undefined}
          >
            {subject}
          </span>
        )}
        <p className="offers-notifications-drawer__msg">{notificationText(notif)}</p>
        <time
          className="offers-notif-item__time"
          dateTime={notif.created_at}
        >
          {sentLabel ? `${sentLabel} · ${relativeTime(notif.created_at)}` : relativeTime(notif.created_at)}
        </time>
      </span>
      {!notif.is_read && (
        <span className="offers-notif-item__unread">
          <span className="offers-notif-item__dot" aria-hidden="true" />
          <Badge variant="neutral" size="sm">New</Badge>
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <li>
        <button
          type="button"
          className={`offers-notif-item offers-notif-item--button${notif.is_read ? '' : ' offers-notif-item--unread'}`}
          onClick={handleClick}
        >
          {inner}
        </button>
      </li>
    );
  }

  return (
    <li>
      <div
        className={`offers-notif-item${notif.is_read ? '' : ' offers-notif-item--unread'}`}
      >
        {inner}
      </div>
    </li>
  );
}

/**
 * NotificationsPanel — the list, toolbar and states the drawer shows; also
 * rendered inline on the Notifications page (App's `notifications` tab).
 *
 * Props:
 *  - notifications: object — the return value of useOfferNotifications
 *  - onOpenHistory?, onNavigate?: as for NotificationsDrawer
 *  - onClose?: () => void — called after a row opens its destination
 */
export function NotificationsPanel({ notifications: n = {}, onOpenHistory, onNavigate, onClose }) {
  // Defaults keep a partial shared state (e.g. a test double) from crashing.
  const noop = () => {};
  const {
    notifications = [],
    unreadCount = 0,
    groupedByDay = {},
    loading = false,
    error = null,
    refetch = noop,
    markAllRead = noop,
    markRead = noop,
  } = n || {};

  const showEmpty = !loading && !error && notifications.length === 0;

  return (
    <div className="offers-notifications-drawer">
      <div className="offers-notifications-drawer__toolbar">
        {unreadCount > 0 && (
          <button
            type="button"
            className="offers-notifications-drawer__btn"
            onClick={markAllRead}
          >
            Mark all read
          </button>
        )}
        <button
          type="button"
          className="offers-notifications-drawer__btn offers-notifications-drawer__btn--icon"
          onClick={refetch}
          aria-label="Refresh notifications"
          title="Refresh"
        >
          <span aria-hidden="true">↻</span>
        </button>
      </div>

      <div
        className="offers-notifications-drawer__content"
        aria-busy={loading || undefined}
      >
        {error && (
          <div role="alert" className="offers-notifications-drawer__error">
            <p>Couldn't load notifications. Try again.</p>
            <button
              type="button"
              className="offers-notifications-drawer__btn"
              onClick={refetch}
            >
              Retry
            </button>
          </div>
        )}

        {loading && notifications.length === 0 && !error && (
          <ul className="offers-notifications-drawer__skeletons">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="offers-notif-skeleton" />
            ))}
          </ul>
        )}

        {showEmpty && (
          <div className="offers-notifications-drawer__empty">
            <div className="offers-notifications-drawer__empty-icon" aria-hidden="true">🔔</div>
            <h3 className="offers-notifications-drawer__empty-title">All caught up</h3>
            <p className="offers-notifications-drawer__empty-body">
              You'll see updates about your offers and contingencies here.
            </p>
          </div>
        )}

        {!showEmpty && !error && GROUP_ORDER.map((group) => {
          const items = groupedByDay[group] || [];
          if (items.length === 0) return null;
          return (
            <section key={group} className="offers-notifications-drawer__group">
              <h3 className="offers-notifications-drawer__group-label">{group}</h3>
              <ul className="offers-notifications-drawer__list">
                {items.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notif={notif}
                    onOpenHistory={onOpenHistory}
                    onNavigate={onNavigate}
                    onMarkRead={markRead}
                    onClose={onClose}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/**
 * NotificationsDrawer — slide-over for offer-related notifications.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - userId: number | string
 *  - onOpenHistory?: ({ offerId }) => void — used when a row has no `link`
 *  - onNavigate?: (tab, { offerId, transactionId?, contingencyId? }) => void —
 *      opens the row's in-app destination: a workspace tab key scoped to the
 *      viewer's side of the deal (e.g. `contingencies:seller`), and the
 *      transaction and contingency it is about
 *  - notifications?: object — optional return value of useOfferNotifications.
 *      When provided (e.g. from NotificationsBell), the drawer reuses it
 *      instead of calling the hook itself, preventing duplicate fetches.
 */
export default function NotificationsDrawer({
  open,
  onClose,
  userId,
  onOpenHistory,
  onNavigate,
  notifications: notificationsProp,
}) {
  // Always call hook to keep hook order stable; pass userId only if no
  // shared notifications object was provided.
  const own = useOfferNotifications({
    userId: notificationsProp ? null : userId,
    pollMs: notificationsProp ? 0 : 60000,
  });

  return (
    <Drawer open={open} onClose={onClose} title="Notifications">
      <NotificationsPanel
        notifications={notificationsProp || own}
        onOpenHistory={onOpenHistory}
        onNavigate={onNavigate}
        onClose={onClose}
      />
    </Drawer>
  );
}
